import { http } from '../request'
import type {
    PermissionStats,
    UserPermissionSummary,
    CleanupRequest,
    CleanupPermissionsResponse,
    CleanupRolesResponse,
    ClearUserCacheRequest,
    ClearRoleCacheRequest,
    CacheCleanupResponse,
    PermissionConstraintSummary,
    UserActivityRequest,
    UserActivityResponse
} from '../types_admin/permission_management'

/**
 * 权限管理 API
 * 对接后端 PermissionManagementViewSet (/api/permissions/management/)
 */
export const adminPermissionManagementApi = {
    /**
     * 获取权限系统统计
     * GET /api/permissions/management/stats/
     */
    getPermissionStats(): Promise<PermissionStats> {
        return http.get('/api/permissions/management/stats/')
    },

    /**
     * 获取用户权限摘要
     * GET /api/permissions/management/user-summary/
     * @param user_id 用户ID（可选，不传则查询当前用户）
     */
    getUserPermissionSummary(user_id?: number): Promise<UserPermissionSummary> {
        const url = user_id 
            ? `/api/permissions/management/user-summary/?user_id=${user_id}`
            : '/api/permissions/management/user-summary/'
        return http.get(url)
    },

    /**
     * 清理过期权限
     * POST /api/permissions/management/cleanup-expired-permissions/
     * @param dry_run 是否为预览模式（默认true）
     */
    cleanupExpiredPermissions(data: CleanupRequest = { dry_run: true }): Promise<CleanupPermissionsResponse> {
        return http.post('/api/permissions/management/cleanup-expired-permissions/', data)
    },

    /**
     * 清理过期角色分配
     * POST /api/permissions/management/cleanup-expired-roles/
     * @param dry_run 是否为预览模式（默认true）
     */
    cleanupExpiredRoles(data: CleanupRequest = { dry_run: true }): Promise<CleanupRolesResponse> {
        return http.post('/api/permissions/management/cleanup-expired-roles/', data)
    },

    /**
     * 清理用户缓存
     * DELETE /api/permissions/management/clear-user-cache/
     * @param user_id 用户ID（可选，不传则清理当前用户缓存）
     */
    clearUserCache(data: ClearUserCacheRequest = {}): Promise<CacheCleanupResponse> {
        return http.post('/api/permissions/management/clear-user-cache/', data)
    },

    /**
     * 清理角色缓存
     * DELETE /api/permissions/management/clear-role-cache/
     * @param role_id 角色ID（必填）
     */
    clearRoleCache(data: ClearRoleCacheRequest): Promise<CacheCleanupResponse> {
        return http.post('/api/permissions/management/clear-role-cache/', data)
    },

    /**
     * 获取权限约束摘要（企业级功能）
     * GET /api/permissions/management/constraint-summary/
     */
    getConstraintSummary(): Promise<PermissionConstraintSummary> {
        return http.get('/api/permissions/management/constraint-summary/')
    },

    /**
     * 获取用户活动记录
     * GET /api/permissions/audit-logs/user-activity/
     * @param params 查询参数
     */
    getUserActivity(params: UserActivityRequest): Promise<UserActivityResponse> {
        const queryString = new URLSearchParams({
            user_id: params.user_id.toString(),
            ...(params.limit && { limit: params.limit.toString() }),
            ...(params.days && { days: params.days.toString() })
        }).toString()
        return http.get(`/api/permissions/audit-logs/user-activity/?${queryString}`)
    }
}
